import {
  ChangeDetectionStrategy,
  Component,
  contentChild,
  forwardRef,
  Input,
  input,
  OnInit,
  output,
  TemplateRef,
  viewChild,
  DestroyRef,
  inject
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import {
  NgbDropdown,
  NgbDropdownMenu,
  NgbDropdownToggle
} from '@ng-bootstrap/ng-bootstrap';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { AppFormFieldControl } from '../../form-field/form-field-control/form-field-control';
import { BaseControl } from '../base-control';
import { InfiniteScrollComponent } from '../../../../atoms/infinite-scroll/infinite-scroll.component';
import { isObject } from '../../../../../utils/type-guards';
import { isValueNotNullAndUndefined } from '../../../../../utils/coercion';
import { objectsEqual } from '../../../../../utils/object-functions';
import { LoadingSpinnerComponent } from '../../../loading-spinner/loading-spinner.component';

/** @deprecated Use the new ImmomioSelect component from @immomio/ui/select instead. */
@Component({
  selector: 'app-dropdown-select',
  templateUrl: './dropdown-select.component.html',
  styleUrls: ['./dropdown-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownSelectComponent),
      multi: true
    },
    {
      provide: AppFormFieldControl,
      useExisting: forwardRef(() => DropdownSelectComponent)
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgbDropdown,
    FormsModule,
    NgbDropdownToggle,
    NgTemplateOutlet,
    NgbDropdownMenu,
    InfiniteScrollComponent,
    LoadingSpinnerComponent,
    TranslatePipe
  ]
})
export class DropdownSelectComponent
  extends BaseControl<any>
  implements OnInit
{
  #destroyRef = inject(DestroyRef);
  private _items: unknown[];
  readonly dropdown = viewChild(NgbDropdown);
  readonly ngControl = viewChild(NgControl);
  readonly templateRef = contentChild(TemplateRef);
  readonly itemValueKey = input('value');
  readonly placement = input('bottom-left');

  @Input() get items(): unknown[] {
    return this._items;
  }

  set items(value) {
    this._items = value || [];
    this.setSelectedItem();
  }

  readonly clearable = input(false);

  // useSelf should be used, when you want to have an object as value
  readonly useSelf = input(false);
  readonly showSeperator = input(false);
  readonly container = input<'body' | null>(null);
  readonly relativePositioned = input(false);
  // use `menuClass` when `container` is set to 'body'
  // and styles need to be applied to dropdown menu from outside:
  readonly menuClass = input<string>(undefined);
  readonly toggleClass = input<string>(undefined);
  readonly isLoadingMenuItems = input(false);
  readonly trackId = input('value');

  readonly scrollBottom = output();

  public uniqueElementId: string;
  public selectedItem: any;

  public isDropdownOpened = false;

  public get isValue() {
    return (
      isValueNotNullAndUndefined(this.value) &&
      this.value !== '' &&
      !Array.isArray(this.value)
    );
  }

  ngOnInit() {
    this.uniqueElementId = `dropdown-select-${crypto.randomUUID()}`;
    this.ngControl()
      .statusChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.stateChanges.next());
  }

  onItemClick(item: any) {
    this.dropdown().close();
    this.value =
      this.useSelf() || !isObject(item) ? item : item[this.itemValueKey()];
    this.setSelectedItem();
    this.stateChanges.next();
  }

  reset($event: Event) {
    // When you don't have this, then the dropdown would be opened/closed
    // It stops the parent element from also receiving the click event
    $event?.stopPropagation();
    this.dropdown().close();
    this.value = null;
    this.selectedItem = null;
  }

  public writeValue(value): void {
    this.value = value;
    this.setSelectedItem();
    this.stateChanges.next();
  }

  onScroll() {
    this.scrollBottom.emit();
  }

  close() {
    this.dropdown().close();
  }

  private setSelectedItem() {
    this.selectedItem = this.items?.find(item => {
      if (isObject(item)) {
        if (item[this.itemValueKey()] === this.value) return true;

        // If we want to have an object as dropdown value
        if (this.useSelf() && objectsEqual(item, this.value)) return true;
      }
      return item === this.value;
    });

    /* When values are updated multiple times during the form load, such as when patching a form,
       there can be an issue where a dropdown only considers the first value update, and subsequent
       updates are ignored. This can result in the label only being updated once, even though the
       underlying value is now different.
       To fix this issue, we manually trigger change detection using the ChangeDetectorRef.
       By manually detecting changes, we can ensure that the component's view reflects the
       current state of the form, even when multiple updates are made in quick succession.
    */

    this.cdr.detectChanges();
  }

  public setShowFilters(event: boolean) {
    this.isDropdownOpened = event;
  }
}
